import React from 'react';
import { HashRouter as Router, Routes, Route, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import SafeIcon from './components/common/SafeIcon';
import AccessGuard from './components/AccessGuard';
import Dashboard from './pages/Dashboard';
import Editor from './pages/Editor';
import Settings from './pages/Settings';
import Recipients from './pages/Recipients';
import Articles from './pages/Articles';
import { 
  getStoredData, 
  saveStoredData, 
  getSettings, 
  saveSettings, 
  getRecipients, 
  saveRecipients, 
  getArticles, 
  saveArticles 
} from './utils/storage';
import './App.css';

const { FiHome, FiPlusCircle, FiSettings, FiUsers, FiDownload, FiUpload, FiPackage } = FiIcons;

// Hilfsfunktion zur Berechnung der optimalen Spaltenbreite
const calculateColumnWidths = (data) => {
  if (!data || data.length === 0) return [];
  
  const headers = Object.keys(data[0]);
  
  return headers.map(key => {
    let maxLen = key.length;
    
    data.forEach(row => {
      const value = row[key] ? String(row[key]) : '';
      const lines = value.split('\n');
      lines.forEach(line => {
        maxLen = Math.max(maxLen, line.length);
      });
    });

    return { wch: Math.min(Math.max(maxLen + 2, 10), 80) };
  });
};

function App() {
  const handleExport = () => {
    const gutschriften = getStoredData();
    const settings = getSettings();
    const recipients = getRecipients();
    const articles = getArticles();

    const wb = XLSX.utils.book_new();

    // 1. Gutschriften
    const gsData = gutschriften.map(g => ({
      ID: g.id,
      Nummer: g.number,
      Datum: g.date,
      Empfänger_Name: g.recipientName,
      Adresse: g.recipientAddress,
      Brutto: g.items.reduce((sum, i) => sum + (i.quantity * i.price), 0) * 1.19
    }));
    const gsSheet = XLSX.utils.json_to_sheet(gsData);
    gsSheet['!cols'] = calculateColumnWidths(gsData);
    XLSX.utils.book_append_sheet(wb, gsSheet, "Gutschriften");

    // 2. Positionen
    const posData = [];
    gutschriften.forEach(g => {
      g.items.forEach(item => {
        posData.push({
          Gutschrift_Nr: g.number,
          Gutschrift_ID: g.id,
          Beschreibung: item.description,
          Menge: item.quantity,
          Preis_Netto: item.price
        });
      });
    });
    const posSheet = XLSX.utils.json_to_sheet(posData);
    posSheet['!cols'] = calculateColumnWidths(posData);
    XLSX.utils.book_append_sheet(wb, posSheet, "Positionen");

    // 3. Stammdaten
    const recSheet = XLSX.utils.json_to_sheet(recipients);
    recSheet['!cols'] = calculateColumnWidths(recipients);
    XLSX.utils.book_append_sheet(wb, recSheet, "Empfaenger");

    const artSheet = XLSX.utils.json_to_sheet(articles);
    artSheet['!cols'] = calculateColumnWidths(articles);
    XLSX.utils.book_append_sheet(wb, artSheet, "Artikel");

    const settingsData = Object.entries(settings).map(([key, value]) => ({
      Einstellung: key,
      Wert: value
    }));
    const setSheet = XLSX.utils.json_to_sheet(settingsData);
    setSheet['!cols'] = calculateColumnWidths(settingsData);
    XLSX.utils.book_append_sheet(wb, setSheet, "Einstellungen");

    const timestamp = format(new Date(), 'dd-MM-yyyy_HH-mm');
    XLSX.writeFile(wb, `Weativ Gutschrift_${timestamp}.xlsx`);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        const gsRaw = XLSX.utils.sheet_to_json(workbook.Sheets['Gutschriften']);
        const posRaw = XLSX.utils.sheet_to_json(workbook.Sheets['Positionen']);
        const recRaw = XLSX.utils.sheet_to_json(workbook.Sheets['Empfaenger']);
        const artRaw = XLSX.utils.sheet_to_json(workbook.Sheets['Artikel']);

        if (gsRaw.length) {
          const reconstructed = gsRaw.map(g => ({
            id: String(g.ID),
            number: g.Nummer,
            date: g.Datum,
            recipientName: g.Empfänger_Name,
            recipientAddress: g.Adresse,
            items: posRaw
              .filter(p => String(p.Gutschrift_ID) === String(g.ID))
              .map((p, idx) => ({
                id: Date.now() + idx,
                description: p.Beschreibung,
                quantity: Number(p.Menge),
                price: Number(p.Preis_Netto)
              }))
          }));
          saveStoredData(reconstructed);
        }

        if (recRaw.length) saveRecipients(recRaw);
        if (artRaw.length) saveArticles(artRaw);

        alert('Daten erfolgreich importiert!');
        window.location.reload();
      } catch (err) {
        alert('Fehler beim Import.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <AccessGuard>
      <Router>
        <div className="min-h-screen bg-gray-50 flex">
          {/* Sidebar */}
          <nav className="w-64 bg-slate-900 text-white p-6 hidden md:flex flex-col">
            <div className="flex items-center gap-3 mb-10">
              <div className="p-2 bg-blue-600 rounded-lg shadow-lg shadow-blue-900/50">
                <SafeIcon icon={FiPlusCircle} className="text-xl" />
              </div>
              <h1 className="text-xl font-bold tracking-tight">Gutschrift Pro</h1>
            </div>

            <div className="space-y-1 flex-1">
              <SidebarLink to="/" icon={FiHome} label="Dashboard" />
              <SidebarLink to="/new" icon={FiPlusCircle} label="Neue Gutschrift" />
              
              <div className="h-4" />
              <p className="px-4 text-[10px] uppercase font-bold text-slate-500 mb-2 tracking-wider">Stammdaten</p>
              <SidebarLink to="/recipients" icon={FiUsers} label="Empfänger" />
              <SidebarLink to="/articles" icon={FiPackage} label="Artikel" />
              <SidebarLink to="/settings" icon={FiSettings} label="Einstellungen" />
            </div>

            <div className="pt-10 border-t border-slate-800 space-y-4">
              <p className="text-xs text-slate-500 uppercase font-semibold">Backup</p>
              <button 
                onClick={handleExport}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
              >
                <SafeIcon icon={FiDownload} /> Exportieren
              </button>
              <label className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">
                <SafeIcon icon={FiUpload} /> Importieren
                <input type="file" className="hidden" accept=".xlsx,.xls" onChange={handleImport} />
              </label>
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto">
            <header className="bg-white border-b px-8 py-4 flex justify-between items-center md:hidden">
              <h1 className="text-xl font-bold">Gutschrift Pro</h1>
              <div className="flex gap-4">
                <Link to="/articles" className="p-2 text-gray-600"><SafeIcon icon={FiPackage} /></Link>
                <Link to="/settings" className="p-2 text-gray-600"><SafeIcon icon={FiSettings} /></Link>
              </div>
            </header>

            <div className="p-4 md:p-8 max-w-6xl mx-auto">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/new" element={<Editor />} />
                <Route path="/edit/:id" element={<Editor />} />
                <Route path="/recipients" element={<Recipients />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </div>
          </main>
        </div>
      </Router>
    </AccessGuard>
  );
}

function SidebarLink({ to, icon, label }) {
  return (
    <Link 
      to={to} 
      className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-800 transition-colors text-slate-300 hover:text-white"
    >
      <SafeIcon icon={icon} />
      <span className="font-medium">{label}</span>
    </Link>
  );
}

export default App;